import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LOCKED_PRODUCTS } from '../../../constants/lockedItems';
import { NO_FILTERED_RESULTS, NO_WAY_TO_CHANGE_THIS_PRODUCT } from '../../../constants/messages';
import { deleteProduct, selectProduct, updateProduct, setDraftProduct } from '../../../store/ProductSlice';
import { AppDispatch } from '../../../store/store';
import { AlertType, FillerType, FabricType, Category, Option, Product, PromoKey } from '../../../types/common';
import { showAlert } from '../../../store/CommonSlice';
import AreaLoader from '../../UI/AreaLoader/AreaLoader';
import Card from '../../UI/Card/Card';
import Chip from '../../UI/Chip/Chip';
import IconButton from '../../UI/IconButton/IconButton';
import CopyIcon from '../../UI/icons/CopyIcon/CopyIcon';
import EditIcon from '../../UI/icons/EditIcon/EditIcon';
import LockIcon from '../../UI/icons/LockIcon/LockIcon';
import TrashIcon from '../../UI/icons/TrashIcon/TrashIcon';
import Placeholder from '../../UI/Placeholder/Placeholder';
import Select from '../../UI/Select/Select';
import Tooltip from '../../UI/Tooltip/Tooltip';
import Button from '../../UI/Button/Button';
import { str, num } from '../../../utils/helpers';
import classes from './ProductsList.module.css';

const getImageUrl = (imageId: string) => `${process.env.REACT_APP_SERVER_URL}/products/images/${imageId}`;

const priorityOptions: Option[] = Array.from({ length: 10 }, (_, i) => ({
    id: String(i + 1),
    name: String(i + 1),
}));

const SEARCH_KEY = 'adminProductsSearch';

interface IProductsListProps {
    products: Product[];
    typesOfFiller: FillerType[];
    typesOfFabric: FabricType[];
    onOpen: () => void;
    isLoading: boolean;
    categories: Category[];
    defaultSort: (list: Product[]) => Product[];
}

type SortDir = 'none' | 'asc' | 'desc';

const PROMO_LABEL_BY_ID: Record<PromoKey, string> = {
    promo: 'Промо',
    season: 'Сезонная',
    sale: 'Распродажа',
    blank: 'Акция',
};

const parseCrptAvailable = (v: unknown): number | null => {
    if (v === null || v === undefined) return null;
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    return Math.max(0, Math.trunc(n));
};
const COLORS = {
    qtyRed: '#d11a2a',
    qtyYellow: '#b58900',
    qtyGreen: '#0a8f3c',
    qtyBlack: '#000000',
    markedBlue: '#1f5bd6',
};

const ProductsList: React.FC<IProductsListProps> = ({ products, onOpen, isLoading, categories, typesOfFiller, typesOfFabric, defaultSort }) => {
    const [productList, setProductList] = useState<Product[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>(() => localStorage.getItem(SEARCH_KEY) ?? '');
    const [selectedCategoryOption, setSelectedCategoryOption] = useState<Option>({});
    const [selectedFillerTypeOption, setSelectedFillerTypeOption] = useState<Option>({});
    const [selectedFabricTypeOption, setSelectedFabricTypeOption] = useState<Option>({});
    const [filteredCategories, setFilteredCategories] = useState<Category[]>(categories);
    const [filteredTypesOfFiller, setFilteredTypesOfFiller] = useState<FillerType[]>(typesOfFiller);
    const [filteredTypesOfFabric, setFilteredTypesOfFabric] = useState<FabricType[]>(typesOfFabric);
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
    const [pageSize, setPageSize] = useState<number>(100);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [sortField, setSortField] = useState<'name' | 'articleNumber' | 'priority' | 'price' | ''>('');
    const [sortDir, setSortDir] = useState<SortDir>('none');
    const dispatch = useDispatch<AppDispatch>();
    // const hasNoFilteredProducts = productList.length === 0;

    useEffect(() => {
        setProductList(defaultSort(products));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [products]);

    useEffect(() => {
        localStorage.setItem(SEARCH_KEY, searchTerm);
    }, [searchTerm]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedCategoryOption, selectedFillerTypeOption, selectedFabricTypeOption]);

    useEffect(() => {
        const selectedCategoryId = String(selectedCategoryOption.id || '');
        const selectedFillerId = String(selectedFillerTypeOption.id || '');
        const selectedFabricId = String(selectedFabricTypeOption.id || '');

        // 1) Список товаров по всем активным фильтрам (AND)
        const filteredByAll = products.filter((p) => {
            const okCategory = !selectedCategoryId || p.category?.id === selectedCategoryId;
            const okFiller = !selectedFillerId || p.fillerType?.id === selectedFillerId;
            const okFabric = !selectedFabricId || p.fabricType?.id === selectedFabricId;
            return okCategory && okFiller && okFabric;
        });
        setProductList(defaultSort(filteredByAll));

        // 2) Доступные категории зависят от выбранных filler + fabric
        const productsForCategories = products.filter((p) => {
            const okFiller = !selectedFillerId || p.fillerType?.id === selectedFillerId;
            const okFabric = !selectedFabricId || p.fabricType?.id === selectedFabricId;
            return okFiller && okFabric;
        });
        const categoryIds = new Set(productsForCategories.map((p) => p.category?.id).filter(Boolean) as string[]);
        const nextFilteredCategories = categories.filter((c) => categoryIds.has(c.id));
        setFilteredCategories(nextFilteredCategories);

        // 3) Доступные filler зависят от выбранных category + fabric
        const productsForFillers = products.filter((p) => {
            const okCategory = !selectedCategoryId || p.category?.id === selectedCategoryId;
            const okFabric = !selectedFabricId || p.fabricType?.id === selectedFabricId;
            return okCategory && okFabric;
        });
        const fillerIds = new Set(productsForFillers.map((p) => p.fillerType?.id).filter(Boolean) as string[]);
        const nextFilteredFillers = typesOfFiller.filter((f) => fillerIds.has(f.id));
        setFilteredTypesOfFiller(nextFilteredFillers);

        // 4) Доступные fabric зависят от выбранных category + filler
        const productsForFabrics = products.filter((p) => {
            const okCategory = !selectedCategoryId || p.category?.id === selectedCategoryId;
            const okFiller = !selectedFillerId || p.fillerType?.id === selectedFillerId;
            return okCategory && okFiller;
        });
        const fabricIds = new Set(productsForFabrics.map((p) => p.fabricType?.id).filter(Boolean) as string[]);
        const nextFilteredFabrics = typesOfFabric.filter((f) => fabricIds.has(f.id));
        setFilteredTypesOfFabric(nextFilteredFabrics);

        // 5) Автосброс выбранных значений, если они стали невалидными
        if (selectedCategoryId && !categoryIds.has(selectedCategoryId)) setSelectedCategoryOption({});
        if (selectedFillerId && !fillerIds.has(selectedFillerId)) setSelectedFillerTypeOption({});
        if (selectedFabricId && !fabricIds.has(selectedFabricId)) setSelectedFabricTypeOption({});

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [products, categories, typesOfFiller, typesOfFabric, selectedCategoryOption.id, selectedFillerTypeOption.id, selectedFabricTypeOption.id]);

    const searchTermNormalized = searchTerm.trim().toLowerCase();
    const visibleList = productList.filter((product) => {
        if (!searchTermNormalized) return true;
        const name = str(product.name).toLowerCase();
        const article = str(product.articleNumber).toLowerCase();
        return name.includes(searchTermNormalized) || article.includes(searchTermNormalized);
    });

    const sortedList = [...visibleList].sort((a, b) => {
        if (sortDir === 'none' || !sortField) return 0;
        const dir = sortDir === 'asc' ? 1 : -1;
        switch (sortField) {
            case 'name':
                return str(a.name).localeCompare(str(b.name)) * dir;
            case 'articleNumber':
                return str(a.articleNumber).localeCompare(str(b.articleNumber)) * dir;
            case 'priority':
                return (num(a.priority) - num(b.priority)) * dir;
            case 'price':
                return (num(a.price) - num(b.price)) * dir;
            default:
                return 0;
        }
    });

    const totalPages = Math.max(1, Math.ceil(sortedList.length / pageSize));
    const paginatedList = sortedList.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const toggleSort = (field: typeof sortField) => {
        if (sortField !== field) {
            setSortField(field);
            setSortDir('desc');
        } else {
            setSortDir((prev) => (prev === 'desc' ? 'asc' : prev === 'asc' ? 'none' : 'desc'));
            if (sortDir === 'none') setSortField(field);
            if (sortDir === 'asc') setSortField('');
        }
    };

    const sortIcon = (field: typeof sortField) => {
        if (sortField !== field || sortDir === 'none') return null;
        return sortDir === 'asc' ? '▲' : '▼';
    };

    const handlePriorityChange = async (productId: Product['id'], option: Option) => {
        if (!option.id) return;
        const newPriority = Number(option.id);
        const product = productList.find((p) => p.id === productId);
        if (!product || product.priority === newPriority) return;
        try {
            await dispatch(updateProduct({ ...product, priority: newPriority })).unwrap();
            dispatch(
                showAlert({
                    type: AlertType.Success,
                    message: 'Приоритет товара успешно обновлён',
                }),
            );
        } catch {
            /* Ошибку обрабатывает extraReducer updateProduct.rejected */
        }
    };

    const handleChangeSelect = (option: Option) => {
        if (option.field === 'category') {
            setSelectedCategoryOption(option.id ? option : {});
            return;
        }
        if (option.field === 'fillerType') {
            setSelectedFillerTypeOption(option.id ? option : {});
            return;
        }
        if (option.field === 'fabricType') {
            setSelectedFabricTypeOption(option.id ? option : {});
            return;
        }
    };

    const deleteProductHandler = (id: Product['id']) => {
        setConfirmDeleteId(id);
    };
    const confirmDelete = () => {
        if (confirmDeleteId) {
            dispatch(deleteProduct(confirmDeleteId));
            setConfirmDeleteId(null);
        }
    };
    const cancelDelete = () => {
        setConfirmDeleteId(null);
    };

    const editProductHandler = (id: Product['id']) => {
        dispatch(selectProduct(id));
        onOpen();
    };

    const copyProductHandler = (id: Product['id']) => {
        const product = products.find((p) => p.id === id);
        if (!product) return;
        const draft: Product = {
            ...product,
            id: '',
            articleNumber: '',
            image: null as any,
            secondImage: null as any,
            thirdImage: null as any,
            fourthImage: null as any,
            fifthImage: null as any,
            sixthImage: null as any,
        };
        (draft as any).__draftKey = `copy:${id}:${Date.now()}`;
        dispatch(setDraftProduct(draft));
        onOpen();
    };

    const clearSearch = () => {
        setSearchTerm('');
        localStorage.removeItem(SEARCH_KEY);
    };

    return (
        <Card fullWidth>
            <>
                {isLoading && <AreaLoader />}
                <div className={classes.paginationControls}>
                    <label className={classes.paginationControlsValue}>
                        Показывать:
                        <select
                            className={classes.paginationControlsValueSelect}
                            value={pageSize}
                            onChange={(e) => {
                                setPageSize(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                        >
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={200}>200</option>
                        </select>
                    </label>
                    <button disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}>
                        ‹
                    </button>
                    <span>
                        {currentPage} / {totalPages}
                    </span>
                    <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}>
                        ›
                    </button>
                </div>

                <table className={classes['adminProdListTable']}>
                    <thead className={classes['adminProdListTable-thead']}>
                        <tr className={classes['adminProdListTable-thead-tr']}>
                            <th className={classes['foto-th']}>Фото</th>
                            <th className={classes.sortable} onClick={() => toggleSort('name')}>
                                Название {sortIcon('name')}
                            </th>
                            <th className={`${classes['article-th']} ${classes.sortable}`} onClick={() => toggleSort('articleNumber')}>
                                Артикул {sortIcon('articleNumber')}
                            </th>
                            <th className={`${classes['priority-th']} ${classes.sortable}`} onClick={() => toggleSort('priority')}>
                                Приоритет {sortIcon('priority')}
                            </th>
                            <th className={classes['size-color-th']}>Размер/Цвет</th>
                            <th className={classes['category-th']}>Категория</th>
                            <th className={classes['fillerType-th']}>Наполнитель</th>
                            <th className={classes['fillerType-th']}>Ткань</th>
                            <th className={classes['qty-th']} title="Количество единиц на складе">
                                Количество
                            </th>
                            <th className={`${classes['price-th']} ${classes.sortable}`} onClick={() => toggleSort('price')} title="Цена за единицу товара">
                                Цена {sortIcon('price')}
                            </th>
                            <th className={classes['action-th']}></th>
                        </tr>
                    </thead>

                    <tbody className={classes['adminProdListTable-tbody']}>
                        <tr className={classes['filter-row']}>
                            <td></td>
                            <td>
                                <div className={classes.searchWrapper}>
                                    <input
                                        className={classes.searchInput}
                                        type="text"
                                        placeholder="Поиск товара"
                                        value={searchTerm}
                                        title="Поиск по названию и артикулу товара"
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    {searchTerm.length > 0 && (
                                        <span className={classes.searchReset} onClick={clearSearch} title="Сбросить поиск">
                                            x
                                        </span>
                                    )}
                                </div>
                            </td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td>
                                <Select
                                    options={filteredCategories}
                                    onSelect={handleChangeSelect}
                                    defaultOptionText={'Выберите категорию'}
                                    label=""
                                    value={selectedCategoryOption.name}
                                    field={'category'}
                                    isDisabled={filteredCategories.length === 0}
                                />
                            </td>
                            <td>
                                <Select
                                    options={filteredTypesOfFiller}
                                    onSelect={handleChangeSelect}
                                    defaultOptionText={'Выберите наполнитель'}
                                    label=""
                                    value={selectedFillerTypeOption.name}
                                    field={'fillerType'}
                                    isDisabled={filteredTypesOfFiller.length === 0}
                                />
                            </td>
                            <td>
                                <Select
                                    options={filteredTypesOfFabric}
                                    onSelect={handleChangeSelect}
                                    defaultOptionText={'Выберите ткань'}
                                    label=""
                                    value={selectedFabricTypeOption.name}
                                    field={'fabricType'}
                                    isDisabled={filteredTypesOfFabric.length === 0}
                                />
                            </td>
                            <td></td>
                            <td></td>
                            <td></td>
                        </tr>

                        {paginatedList.length === 0 && (
                            <tr>
                                <td colSpan={11}>
                                    <Placeholder text={NO_FILTERED_RESULTS} size={'24px'} />
                                </td>
                            </tr>
                        )}

                        {paginatedList.map(
                            ({
                                id,
                                image,
                                price,
                                fillerType,
                                fabricType,
                                category,
                                name,
                                nameDescription,
                                discount,
                                specialDiscount,
                                articleNumber,
                                priority,
                                quantity,
                                group,
                                promoType,
                                crptAvailable,
                            }) => {
                                const qtyNum = Math.max(0, Math.trunc(num(quantity)));
                                const markedNum = parseCrptAvailable(crptAvailable);
                                const hasMarked = markedNum !== null;
                                let qtyColor = COLORS.qtyBlack;
                                if (!hasMarked) {
                                    qtyColor = COLORS.qtyBlack;
                                } else if (qtyNum === 0 && (markedNum ?? 0) === 0) {
                                    qtyColor = COLORS.qtyRed;
                                } else if ((markedNum ?? 0) === qtyNum) {
                                    qtyColor = COLORS.qtyGreen;
                                } else {
                                    qtyColor = COLORS.qtyYellow;
                                }
                                return (
                                    <tr key={id}>
                                        <td className={classes.cell}>
                                            <img className={classes.image} title="Главная иллюстрация продукта" src={getImageUrl(image)} alt={name} />
                                        </td>
                                        <td
                                            className={classes['product-name-cell']}
                                            title={`${name}\n${nameDescription ? `Детали: ${nameDescription}` : ''}\n${
                                                group && group.optionValue ? `${group.url}: ${group.optionValue}` : ''
                                            }`}
                                        >
                                            {name}
                                            <div className={classes['chip-wrapper']}>
                                                {discount && <Chip text={'Скидка  -' + discount.percent + '%'} mode={'highlighted'} />}
                                                {promoType && PROMO_LABEL_BY_ID[promoType as keyof typeof PROMO_LABEL_BY_ID] && (
                                                    <Chip text={`Акция: "${PROMO_LABEL_BY_ID[promoType as keyof typeof PROMO_LABEL_BY_ID]}"`} mode={'highlighted'} />
                                                )}
                                            </div>
                                        </td>
                                        <td className={classes.cell}>{articleNumber}</td>
                                        <td style={{ textAlign: 'center' }} className={classes.cell}>
                                            <Select
                                                options={priorityOptions}
                                                onSelect={(option) => handlePriorityChange(id, option)}
                                                defaultOptionText={'—'}
                                                label=""
                                                value={String(priority)}
                                                field={'priority'}
                                                isDisabled={false}
                                            />
                                        </td>
                                        <td className={classes.cell}>{group && (group.url === 'Размер' || group.url === 'Цвет') ? group.optionValue || '' : ''}</td>
                                        <td className={classes.cell}>{category.name}</td>
                                        <td className={classes.cell}>{fillerType?.name}</td>
                                        <td className={classes.cell}>{fabricType?.name ?? '—'}</td>
                                        {/* <td style={{ textAlign: 'center', color: quantity < 1 ? 'red' : 'inherit' }} className={classes.cell}>
                                            {quantity ?? '—'}
                                        </td> */}
                                        <td
                                            style={{ textAlign: 'center' }}
                                            className={classes.cell}
                                            title={hasMarked ? `Кол-во: ${qtyNum}\nПромаркировано: ${markedNum}` : 'Количество'}
                                        >
                                            <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.15 }}>
                                                <span style={{ color: qtyColor, fontWeight: 700 }}>{qtyNum}</span>
                                                {hasMarked && (
                                                    <span style={{ color: COLORS.markedBlue, fontSize: '12px', marginTop: '2px', fontWeight: 600 }}>
                                                        м: {markedNum}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td
                                            style={{ userSelect: 'none' }}
                                            className={classes.cell}
                                            title={
                                                !discount && !specialDiscount
                                                    ? ''
                                                    : `${discount ? `По скидке ${discount?.percent || '-'}% = ${discount?.discountedPrice || '-'} ₽ ` : ''}${
                                                          specialDiscount
                                                              ? `\nПо спец.скидке ${specialDiscount?.percent || '-'}% = ${specialDiscount?.discountedPrice || '-'} ₽`
                                                              : ''
                                                      }`
                                            }
                                        >
                                            {price} ₽
                                        </td>
                                        <td className={classes['action-cell']}>
                                            <div className={classes.action}>
                                                {LOCKED_PRODUCTS.includes(id) ? (
                                                    <Tooltip icon={<LockIcon />} message={NO_WAY_TO_CHANGE_THIS_PRODUCT} />
                                                ) : (
                                                    <div className={classes.actionGroup}>
                                                        <IconButton onClick={() => copyProductHandler(id)} title="Копировать данные товара для создания нового">
                                                            <CopyIcon />
                                                        </IconButton>

                                                        <IconButton onClick={() => editProductHandler(id)} title="Редактировать товар">
                                                            <EditIcon />
                                                        </IconButton>

                                                        <IconButton onClick={() => deleteProductHandler(id)} title="Удалить товар">
                                                            <TrashIcon fill={'lightgray'} />
                                                        </IconButton>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            },
                        )}
                    </tbody>
                </table>

                {confirmDeleteId && (
                    <div className={classes.modalOverlay}>
                        <div className={classes.modalContent}>
                            <p>Вы уверены, что хотите удалить этот товар?</p>
                            <div className={classes.modalActions}>
                                <Button mode="primary" onClick={confirmDelete}>
                                    Да
                                </Button>
                                <Button mode="primary" onClick={cancelDelete}>
                                    Отмена
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </>
        </Card>
    );
};

export default ProductsList;
